/* Small enhancements for the product info column:
   - rc-variant-summary: live-updates the "Colour: X" / "Size: Y" labels on variant change.
   - rc-buy-now: "Buy now" — checkout with only the selected variant via a cart permalink.
   - rc-preorder: pre-order selling plan + [data-preorder] state for the selected variant. */

class RcVariantSummary extends HTMLElement {

  connectedCallback() {
    const cfg = window.umbrellaConfig || {};
    this.event = (cfg.events && cfg.events.variantChange) || 'variant:change';
    this._handler = (e) => this.update(e.detail && e.detail.data);
    document.addEventListener(this.event, this._handler);
  }

  disconnectedCallback() {
    document.removeEventListener(this.event, this._handler);
  }

  update(variant) {
    if (!variant) return;
    this.querySelectorAll('[data-option-position]').forEach((el) => {
      const pos = el.getAttribute('data-option-position');
      const value = variant['option' + pos];
      if (value != null && value !== '') el.textContent = value;
    });
  }

}

if (!customElements.get('rc-variant-summary')) customElements.define('rc-variant-summary', RcVariantSummary);


class RcBuyNow extends HTMLElement {

  connectedCallback() {
    this.form = this.closest('form');
    this.button = this.querySelector('button');
    if (!this.form || !this.button) return;
    this.soldOut = this.button.disabled;

    // Hidden while the selected variant is sold out (the ATC button says "Out of stock").
    const events = (window.umbrellaConfig && window.umbrellaConfig.events) || {};
    this.variantEvent = events.variantChange || 'variant:change';
    this._onVariant = (e) => {
      const variant = e.detail && e.detail.data;
      if (variant && typeof variant.available === 'boolean') {
        this.soldOut = !variant.available;
        this.button.disabled = this.soldOut;
      }
    };
    document.addEventListener(this.variantEvent, this._onVariant);

    // Coming back from checkout via the back button restores the page from bfcache with the button still disabled.
    this._onPageShow = (e) => { if (e.persisted) this.button.disabled = this.soldOut; };
    window.addEventListener('pageshow', this._onPageShow);

    this._onClick = () => this.buy();
    this.button.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    document.removeEventListener(this.variantEvent, this._onVariant);
    window.removeEventListener('pageshow', this._onPageShow);
    if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
  }

  buy() {
    const idInput = this.form.querySelector('input[name="id"]');
    const variantId = idInput && idInput.value;
    if (!variantId || this.button.disabled) return;
    const qtyInput = this.form.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.button.disabled = true;
    window.location.href = `${root}cart/${variantId}:${quantity}`;
  }

}

if (!customElements.get('rc-buy-now')) customElements.define('rc-buy-now', RcBuyNow);


/* rc-preorder: keeps the pre-order selling plan (Notify Me app) in sync with the selected variant.
   Variants on pre-order send `selling_plan` with add-to-cart; the closest [data-preorder] scope gets
   "true"/"false" so CSS swaps the badge, shipping note and button label. */
class RcPreorder extends HTMLElement {

  connectedCallback() {
    this.input = this.querySelector('input[name="selling_plan"]');
    this.scope = this.closest('[data-preorder]');
    try {
      this.plans = JSON.parse(this.querySelector('[data-preorder-plans]').textContent) || {};
    } catch (e) {
      this.plans = {};
    }
    const cfg = window.umbrellaConfig || {};
    this.event = (cfg.events && cfg.events.variantChange) || 'variant:change';
    this._handler = (e) => this.update(e.detail && e.detail.data);
    document.addEventListener(this.event, this._handler);
    this.applyAppNote();
  }

  disconnectedCallback() {
    document.removeEventListener(this.event, this._handler);
  }

  // The shipping note is the pre-order text set in the Notify Me app — only there when its app embed is on.
  applyAppNote() {
    const note = this.scope && this.scope.querySelector('[data-preorder-note]');
    const app = window._ReStockConfig;
    if (!note || !app) return;
    // The embed's inline config has it in _metafields until the app SDK loads and moves it to pre_order.
    let preOrder = app.pre_order || (app._metafields && app._metafields.preOrderConfig);
    if (typeof preOrder === 'string') {
      try { preOrder = JSON.parse(preOrder); } catch (e) { return; }
    }
    if (!preOrder || !preOrder.product_page) return;
    const contents = preOrder.product_page.ui_contents || {};
    const locale = (window.Shopify && window.Shopify.locale) || app.default_language;
    const text = (contents[locale] || contents[app.default_language] || contents.en || {}).widget_description;
    if (text) note.textContent = text;
  }

  update(variant) {
    // Variant changes from other products on the page (shop the look, quick add) aren't ours.
    if (!variant || !(String(variant.id) in this.plans)) return;
    const plan = this.plans[String(variant.id)];
    if (this.input) {
      this.input.value = plan || '';
      this.input.disabled = !plan;
    }
    if (this.scope) this.scope.dataset.preorder = plan ? 'true' : 'false';
  }

}

if (!customElements.get('rc-preorder')) customElements.define('rc-preorder', RcPreorder);
